import { jsx, jsxs } from "react/jsx-runtime";
import { createRef } from "react";
import assert from "assert";
import { Column } from "../Column/Column.mjs";
import { Columns } from "../Columns/Columns.mjs";
import { Dropdown } from "../Dropdown/Dropdown.mjs";
import { FieldGroup } from "../private/FieldGroup/FieldGroup.mjs";
const defaultMonthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];
const getMonths = (monthNames) => monthNames.map((monthName, i) => ({ label: monthName, value: (i + 1).toString() }));
const getYears = (min, max, ascending) => [...new Array(max - min + 1)].map((_v, i) => {
  const yearStr = String(ascending ? i + min : max - i);
  return { label: yearStr, value: yearStr };
});
const currYear = (/* @__PURE__ */ new Date()).getFullYear();
const makeChangeHandler = (onChange, value, fieldType) => (newValue) => {
  if (typeof onChange === "function") {
    onChange(
      {
        month: {
          year: value && value.year ? value.year : void 0,
          month: parseInt(newValue, 10) || void 0
        },
        year: {
          month: value && value.month ? value.month : void 0,
          year: parseInt(newValue, 10) || void 0
        }
      }[fieldType]
    );
  }
};
const MonthPicker = ({
  id,
  value,
  onChange,
  onBlur,
  onFocus,
  tone,
  disabled,
  minYear = currYear - 100,
  maxYear = currYear,
  ascendingYears = false,
  monthLabel = "Month",
  yearLabel = "Year",
  monthNames = defaultMonthNames,
  ...restProps
}) => {
  assert(monthNames.length === 12, "monthNames array must contain 12 items");
  const currentValue = {
    month: value && value.month ? value.month : void 0,
    year: value && value.year ? value.year : void 0
  };
  const monthRef = createRef();
  const yearRef = createRef();
  const monthId = `${id}-month`;
  const yearId = `${id}-year`;
  const blurHandler = onBlur ? (event) => {
    const fireIfExternal = (element) => {
      if (element !== monthRef.current && element !== yearRef.current) {
        onBlur();
      }
    };
    fireIfExternal(event.relatedTarget);
  } : void 0;
  const focusHandler = onFocus ? (event) => {
    if (event.relatedTarget !== monthRef.current && event.relatedTarget !== yearRef.current) {
      onFocus();
    }
  } : void 0;
  const customFieldGroup = /* @__PURE__ */ jsx(FieldGroup, { id, tone, disabled, ...restProps, children: (fieldGroupProps) => {
    var _a, _b;
    return /* @__PURE__ */ jsxs(Columns, { space: "small", children: [
      /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(
        Dropdown,
        {
          id: monthId,
          value: ((_a = currentValue.month) == null ? void 0 : _a.toString()) || "",
          onChange: makeChangeHandler(onChange, value, "month"),
          onBlur: blurHandler,
          onFocus: focusHandler,
          tone,
          label: monthLabel,
          "aria-label": monthLabel,
          ...fieldGroupProps,
          options: getMonths(monthNames),
          ref: monthRef
        }
      ) }),
      /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(
        Dropdown,
        {
          id: yearId,
          value: ((_b = currentValue.year) == null ? void 0 : _b.toString()) || "",
          onChange: makeChangeHandler(onChange, value, "year"),
          onBlur: blurHandler,
          onFocus: focusHandler,
          tone,
          label: yearLabel,
          "aria-label": yearLabel,
          ...fieldGroupProps,
          options: getYears(minYear, maxYear, ascendingYears),
          ref: yearRef
        }
      ) })
    ] });
  } });
  return customFieldGroup;
};
MonthPicker.displayName = "MonthPicker";
export {
  MonthPicker
};
